import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function AccessPage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Add Schema.org JSON-LD for Access page
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Place",
      "name": "大家行政書士事務所",
      "description": "東京都新宿区神楽坂の大家行政書士事務所へのアクセス情報",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "水道町3-14 神楽坂Nビル 2-M",
        "addressLocality": "新宿区",
        "addressRegion": "東京都",
        "postalCode": "162-0811",
        "addressCountry": "JP"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "35.7089456",
        "longitude": "139.7281234"
      },
      "url": `${siteUrl}/access`,
      "telephone": "+81-90-4055-7489",
      "openingHoursSpecification": [
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          "opens": "09:00",
          "closes": "18:00"
        }
      ],
      "hasMap": "https://www.google.com/maps/search/?api=1&query=東京都新宿区水道町3-14+神楽坂Nビル"
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const accessMethods = [
    {
      icon: 'ri-subway-line',
      title: '東京メトロ有楽町線',
      description: '江戸川橋駅 4番出口より徒歩約3分',
      color: 'bg-amber-50',
      iconColor: 'text-amber-600'
    },
    {
      icon: 'ri-train-line',
      title: '東京メトロ東西線',
      description: '神楽坂駅 1番出口より徒歩約7分',
      color: 'bg-blue-50',
      iconColor: 'text-blue-600'
    }
  ];

  const officeInfo = [
    {
      icon: 'ri-map-pin-line',
      label: '住所',
      value: '〒162-0811 東京都新宿区水道町3-14 神楽坂Nビル 2-M'
    },
    {
      icon: 'ri-phone-line',
      label: '電話番号',
      value: '090-4055-7489',
      link: 'tel:09040557489'
    },
    {
      icon: 'ri-mail-line',
      label: 'メールアドレス',
      value: 'manabu.oya35@gmail.com',
      link: 'mailto:manabu.oya35@gmail.com'
    },
    {
      icon: 'ri-time-line',
      label: '営業時間',
      value: '平日 9:00 - 18:00（土日祝日は予約制）'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white/90 backdrop-blur-sm'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src="https://public.readdy.ai/ai/img_res/d0770232-3388-461a-b5ee-3765a5289f6e.png" alt="大家行政書士事務所" className="h-12 w-auto" />
              <span className="text-xl font-bold text-slate-900">大家行政書士事務所</span>
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/#services" className="text-sm text-slate-700 hover:text-slate-900 whitespace-nowrap transition-colors">サービス案内</Link>
              <Link to="/#about" className="text-sm text-slate-700 hover:text-slate-900 whitespace-nowrap transition-colors">事務所紹介</Link>
              <Link to="/#contact" className="text-sm text-slate-700 hover:text-slate-900 whitespace-nowrap transition-colors">お問い合わせ</Link>
            </div>
            <div className="flex items-center gap-4">
              <a href="tel:09040557489" className="hidden md:flex items-center gap-2 text-sm text-slate-700 hover:text-slate-900 whitespace-nowrap transition-colors">
                <i className="ri-phone-line"></i>
                <span>090-4055-7489</span>
              </a>
              <Link to="/#contact" className="bg-slate-900 text-white px-6 py-2.5 rounded-full text-sm font-medium hover:bg-slate-800 transition-colors whitespace-nowrap flex items-center gap-2">
                無料相談予約
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-block border border-slate-300 px-4 py-1.5 rounded-full text-xs text-slate-600 mb-6">
              ACCESS
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6">
              アクセス
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              江戸川橋駅から徒歩約3分。アクセス便利な立地で、お気軽にお越しいただけます
            </p>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="rounded-3xl overflow-hidden shadow-2xl">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3239.8808641975!2d139.72812347082!3d35.70894557101!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x60188d1e1e1e1e1e%3A0x1e1e1e1e1e1e1e1e!2z56We5qW95Z2CTuODk-ODqyAy6ZqO!5e0!3m2!1sja!2sjp!4v1234567890123!5m2!1sja!2sjp&markers=color:red%7Clabel:●%7C35.7089456,139.7281234&zoom=17"
              width="100%"
              height="600"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="大家行政書士事務所の地図"
            ></iframe>
          </div>
          <div className="mt-6 text-center">
            <a 
              href="https://www.google.com/maps/search/?api=1&query=東京都新宿区水道町3-14+神楽坂Nビル" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-slate-700 hover:text-slate-900 transition-colors"
            >
              <i className="ri-map-pin-line"></i>
              <span className="text-sm font-medium">Google マップで開く</span>
              <i className="ri-external-link-line text-xs"></i>
            </a>
          </div>
        </div>
      </section>

      {/* Access Methods */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              最寄駅
            </h2>
            <p className="text-base text-slate-600">
              2つの路線からアクセス可能です
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {accessMethods.map((method, index) => (
              <div key={index} className={`${method.color} rounded-2xl p-8 hover:shadow-lg transition-shadow`}>
                <div className={`w-16 h-16 ${method.color} rounded-2xl flex items-center justify-center mb-6`}>
                  <i className={`${method.icon} text-3xl ${method.iconColor}`}></i>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{method.title}</h3>
                <p className="text-base text-slate-700">{method.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Office Information */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-slate-900 mb-8">
                事務所情報
              </h2>
              <div className="space-y-6">
                {officeInfo.map((info, index) => (
                  <div key={index} className="flex items-start gap-4 p-6 bg-slate-50 rounded-2xl hover:bg-slate-100 transition-colors">
                    <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className={`${info.icon} text-2xl text-slate-900`}></i>
                    </div>
                    <div className="flex-1">
                      <div className="text-sm text-slate-600 mb-1">{info.label}</div>
                      {info.link ? (
                        <a href={info.link} className="text-base font-medium text-slate-900 hover:text-slate-700 transition-colors">
                          {info.value}
                        </a>
                      ) : (
                        <div className="text-base font-medium text-slate-900">{info.value}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img 
                src="https://static.readdy.ai/image/fa61325f1bcb10e63a715068dd996489/6b88d7f074417848a3418c907c45708d.png" 
                alt="神楽坂Nビル - 大家行政書士事務所"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Directions */}
      <section className="py-20 bg-amber-50/30">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              道順（江戸川橋駅より）
            </h2>
            <p className="text-base text-slate-600">
              江戸川橋駅からの詳しいアクセス方法をご案内します
            </p>
          </div>
          <div className="bg-white rounded-2xl p-8 shadow-sm">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-map-pin-line text-amber-600"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-4">東京メトロ有楽町線 江戸川橋駅から</h3>
                <p className="text-base text-slate-700 leading-relaxed">
                  江戸川橋駅4番出口を出て、目白通りを左方向へ直進。約150m進むと右手に神楽坂Nビルがあります。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Parking Information */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-slate-50 rounded-3xl p-12">
            <div className="flex items-start gap-6">
              <div className="w-16 h-16 bg-slate-900 rounded-2xl flex items-center justify-center flex-shrink-0">
                <i className="ri-parking-box-line text-3xl text-white"></i>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">駐車場のご案内</h3>
                <p className="text-base text-slate-700 leading-relaxed mb-4">
                  専用駐車場はございませんが、近隣にコインパーキングが複数ございます。お車でお越しの際は、事前にご連絡いただければ最寄りの駐車場をご案内いたします。
                </p>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <i className="ri-information-line"></i>
                  <span>駐車料金は30分300円程度です</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-900">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            ご来所の際は<br />
            事前予約をお願いします
          </h2>
          <p className="text-lg text-white/80 mb-8">
            スムーズなご対応のため、お電話またはメールにて事前にご予約ください
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:09040557489" className="inline-flex items-center justify-center gap-2 bg-white text-slate-900 px-8 py-4 rounded-full text-base font-medium hover:bg-slate-100 transition-colors whitespace-nowrap">
              <i className="ri-phone-line"></i>
              電話で予約
            </a>
            <Link to="/#contact" className="inline-flex items-center justify-center gap-2 bg-amber-500 text-slate-900 px-8 py-4 rounded-full text-base font-medium hover:bg-amber-400 transition-colors whitespace-nowrap">
              <i className="ri-mail-line"></i>
              メールで予約
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img src="https://public.readdy.ai/ai/img_res/d0770232-3388-461a-b5ee-3765a5289f6e.png" alt="大家行政書士事務所" className="h-10 w-auto brightness-0 invert" />
              </div>
              <p className="text-sm text-slate-400">
                東京・新宿で信頼される法務パートナー
              </p>
            </div>
            <div>
              <h3 className="font-bold mb-4 text-base">サービス</h3>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><Link to="/#services" className="hover:text-white transition-colors">会社法務</Link></li>
                <li><Link to="/#services" className="hover:text-white transition-colors">許認可</Link></li>
                <li><Link to="/#services" className="hover:text-white transition-colors">民事法務</Link></li>
                <li><Link to="/#services" className="hover:text-white transition-colors">国際業務</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold mb-4 text-base">事務所案内</h3>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><Link to="/#about" className="hover:text-white transition-colors">代表挨拶</Link></li>
                <li><Link to="/access" className="hover:text-white transition-colors">アクセス</Link></li>
                <li><Link to="/#contact" className="hover:text-white transition-colors">料金表</Link></li>
                <li><Link to="/#contact" className="hover:text-white transition-colors">よくある質問</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold mb-4 text-base">お問い合わせ</h3>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><Link to="/#contact" className="hover:text-white transition-colors">無料相談</Link></li>
                <li><a href="tel:09040557489" className="hover:text-white transition-colors">電話相談</a></li>
                <li><a href="mailto:manabu.oya35@gmail.com" className="hover:text-white transition-colors">メール相談</a></li>
                <li><Link to="/#contact" className="hover:text-white transition-colors">予約フォーム</Link></li>
              </ul>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-slate-800 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-slate-400">
              © 大家行政書士事務所 2025 All rights reserved
            </p>
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-sm text-slate-400 hover:text-white transition-colors">
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
